import React, { useMemo, useCallback, useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { LicenseManager, TreeDataModule, RangeSelectionModule, ClipboardModule, RowGroupingModule, RichSelectModule, MenuModule, ColumnsToolPanelModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'sonner';
import { computeTaskStatus, STATUS_STYLES, TASK_STATUSES } from './taskStatus';

// Worst first, so the strip reads as a health bar.
const STRIP_ORDER = ['Overdue', 'Not Started – Overdue', 'Delayed', 'At Risk', 'Late Start',
                     'Forecast Delay', 'On Track', 'Completed – Late', 'Completed – On Time',
                     'Not Started'];
import api from '@/lib/api';
import { ChevronRight, ChevronDown, ChevronLeft, Diamond, Calendar as CalendarIcon, Users, Flag, Link2, Unlink, X, Plus, Search, Copy, Scissors, Clipboard, Clock, Check, CalendarDays, User, Filter, ChevronUp, RotateCcw, Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import WBSToolbar from './WBSToolbar';
import ExcelImportExportDialog from './ExcelImportExportDialog';
import ConnectorScheduleImportDialog from './ConnectorScheduleImportDialog';
import SlipRecoveryCell from './SlipRecoveryCell';
import { MacroLinksDrawer, DisciplineChips } from './MacroLinksDrawer';

// Register AG Grid modules - MUST be done before any grid is instantiated
ModuleRegistry.registerModules([
  AllCommunityModule,
  TreeDataModule,
  RangeSelectionModule,
  ClipboardModule,
  RowGroupingModule,
  RichSelectModule,
  MenuModule,
  ColumnsToolPanelModule
]);

// AG Grid Enterprise License (evaluation/development)
LicenseManager.setLicenseKey('');

// =====================
// CUSTOM CELL RENDERERS
// =====================

// Task Name renderer — shows the task name plus a small paperclip icon to attach documents
const TaskNameRenderer = (props) => {
  const { value, data, context } = props;
  const onAttach = context?.onAttachDocs;
  const attachedCount = context?.attachmentCounts?.[data?.id] || 0;
  return (
    <div className="flex items-center gap-1 h-full min-w-0">
      <span className="truncate flex-1">{value}</span>
      {onAttach && !data?.is_summary && (
        <button
          type="button"
          title={attachedCount > 0 ? `${attachedCount} document(s) attached — click to manage` : 'Attach documents'}
          onClick={(e) => { e.stopPropagation(); onAttach(data); }}
          className={`inline-flex items-center gap-0.5 shrink-0 ${attachedCount > 0 ? 'text-sky-400' : 'text-slate-500 hover:text-sky-300'} opacity-70 hover:opacity-100`}
          data-testid={`attach-docs-${data?.id}`}
          style={{ padding: '2px 4px', borderRadius: 3 }}
        >
          <Paperclip className="w-3 h-3" />
          {attachedCount > 0 && <span className="text-[10px] font-mono">{attachedCount}</span>}
        </button>
      )}
    </div>
  );
};

// Milestone checkbox renderer
const MilestoneRenderer = (props) => {
  const { value, node, data } = props;
  const isSummary = data?.is_summary;
  const handleChange = (e) => {
    if (isSummary) return;
    const newValue = e.target.checked;
    node.setDataValue('is_milestone', newValue);
    if (newValue) {
      node.setDataValue('duration', 0);
    }
  };
  if (isSummary) return null;
  return (
    <div className="flex items-center justify-center h-full">
      {value ? (
        <Diamond className="w-4 h-4 text-[#1E4E8C] fill-[#1E4E8C]" />
      ) : (
        <input 
          type="checkbox" 
          checked={!!value} 
          onChange={handleChange}
          className="w-4 h-4 accent-[#1E4E8C] cursor-pointer"
        />
      )}
    </div>
  );
};

// Duration renderer — supports 3 sources in priority order:
//   1) data.duration (canonical, in hours or days based on duration_unit)
//   2) computed from (scheduled_finish - scheduled_start) when the row was
//      imported with dates only (Aerovex / most XER / MSP imports)
//   3) computed from (baseline_finish - baseline_start) as last fallback
// This keeps the DUR column populated for every task, not just summaries.
const DurationRenderer = (props) => {
  const { value, data } = props;
  if (data?.is_milestone) return <span className="text-slate-500">0d</span>;

  const computeDays = (start, finish) => {
    if (!start || !finish) return null;
    const s = new Date(start);
    const f = new Date(finish);
    if (Number.isNaN(s.getTime()) || Number.isNaN(f.getTime())) return null;
    const ms = f - s;
    if (ms < 0) return null;
    return Math.max(1, Math.round(ms / 86400000));
  };

  let days = null;
  if (value || value === 0) {
    const durationUnit = data?.duration_unit || 'hours';
    days = durationUnit === 'hours' ? value / 8 : value;
  } else {
    days = computeDays(data?.scheduled_start, data?.scheduled_finish)
        ?? computeDays(data?.baseline_start, data?.baseline_finish);
  }
  if (days === null || days === undefined) return '';
  const rounded = Math.round(days * 10) / 10;
  return `${rounded % 1 === 0 ? Math.round(rounded) : rounded}d`;
};

// Date renderer
const DateRenderer = (props) => {
  const { value } = props;
  if (!value) return <span className="text-slate-600">-</span>;
  try {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  } catch {
    return value;
  }
};

// Predecessor renderer with badge style
const PredecessorRenderer = (props) => {
  const { value, context } = props;
  if (!value || !Array.isArray(value) || value.length === 0) return <span className="text-slate-600">-</span>;
  
  const allTasks = context?.tasks || [];
  const taskMap = Object.fromEntries(allTasks.map(t => [t.id, t]));
  const typeMap = { 'finish_to_start': 'FS', 'start_to_start': 'SS', 'finish_to_finish': 'FF', 'start_to_finish': 'SF' };
  
  return (
    <div className="flex items-center gap-1 overflow-hidden">
      {value.slice(0, 3).map((dep, idx) => {
        const predTask = taskMap[dep.predecessor_id];
        if (!predTask) return null;
        const typeCode = typeMap[dep.type] || 'FS';
        const lag = dep.lag ? `+${dep.lag}` : '';
        return (
          <span key={idx} className="px-1.5 py-0.5 bg-[#1E4E8C]/20 text-[#1E4E8C] rounded text-xs font-medium">
            {predTask.uid}{typeCode}{lag}
          </span>
        );
      })}
      {value.length > 3 && <span className="text-slate-500 text-xs">+{value.length - 3}</span>}
    </div>
  );
};

// Calendar renderer
const CalendarRenderer = (props) => {
  const { value, context } = props;
  if (!value) return <span className="text-slate-600">-</span>;
  const calendars = context?.calendars || [];
  const calendar = calendars.find(c => c.id === value);
  return (
    <div className="flex items-center gap-1.5">
      <CalendarIcon className="w-3 h-3 text-slate-500 shrink-0" />
      <span className="truncate">{calendar?.name || 'Unknown'}</span>
    </div>
  );
};

// Resource renderer
const ResourceRenderer = (props) => {
  const { data, context } = props;
  const assignments = data?.resource_assignments || [];
  if (!assignments.length) return <span className="text-slate-600">-</span>;
  
  const resources = context?.resources || [];
  const resourceMap = Object.fromEntries(resources.map(r => [r.id, r]));
  
  // Each assignment can be either a plain resource_id string (legacy) or
  // an object `{resource_id, units, work, cost, ...}` (MPP/XER imports).
  const resolveId = (a) => (typeof a === 'string' ? a : (a && a.resource_id));
  
  return (
    <div className="flex items-center gap-1 overflow-hidden">
      {assignments.slice(0, 2).map((a, idx) => {
        const resource = resourceMap[resolveId(a)];
        return (
          <span key={idx} className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
            {resource?.initials || resource?.name?.substring(0, 2) || '?'}
          </span>
        );
      })}
      {assignments.length > 2 && <span className="text-slate-500 text-xs">+{assignments.length - 2}</span>}
    </div>
  );
};

// Constraint renderer
const ConstraintRenderer = (props) => {
  const { value } = props;
  const labels = {
    'as_soon_as_possible': 'ASAP', 'as_late_as_possible': 'ALAP',
    'start_no_earlier_than': 'SNET', 'finish_no_earlier_than': 'FNET',
    'must_start_on': 'MSO', 'must_finish_on': 'MFO'
  };
  return <span className="text-slate-400">{labels[value] || 'ASAP'}</span>;
};

// Progress renderer with bar
const ProgressRenderer = (props) => {
  const { value } = props;
  const percent = value || 0;
  const color = percent === 100 ? 'bg-emerald-500' : percent > 0 ? 'bg-[#1E4E8C]' : 'bg-slate-600';
  return (
    <div className="flex items-center gap-2 h-full px-1">
      <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
      <span className="text-xs text-slate-400 w-7 text-right">{percent}%</span>
    </div>
  );
};

// =====================
// STATUS RENDERER — the shared ladder in taskStatus.js
// =====================
const StatusRenderer = (props) => {
  const { data } = props;
  if (!data) return <span className="text-slate-500">-</span>;
  const status = computeTaskStatus(data);
  const style = STATUS_STYLES[status] || STATUS_STYLES['Not Started'];
  return (
    <div className="flex items-center h-full px-1" title={status}>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.text} ${style.bg}`}
            data-testid={`task-status-${data.id || ''}`}>
        {style.short}
      </span>
    </div>
  );
};

// =====================
// DATE PICKER CELL EDITOR
// =====================
const DatePickerCellEditor = forwardRef((props, ref) => {
  // Compute initial value
  const getInitialValue = () => {
    if (props.value) {
      try {
        const date = new Date(props.value);
        return date.toISOString().split('T')[0]; // YYYY-MM-DD format
      } catch {
        return '';
      }
    }
    return '';
  };
  
  // Use state for the value
  const [dateValue, setDateValue] = useState(getInitialValue);
  
  // AG Grid v31.1+ API: use onValueChange prop to update the grid
  // This replaces the old getValue() pattern
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    console.log('DatePicker date changed to:', newDate);
    setDateValue(newDate);
    
    // Convert to ISO string for the grid (or null for empty)
    let isoValue = null;
    if (newDate) {
      try {
        isoValue = new Date(newDate + 'T00:00:00Z').toISOString();
      } catch {
        isoValue = null;
      }
    }
    
    // Notify grid of the new value using onValueChange (v31.1+ pattern)
    if (props.onValueChange) {
      console.log('Calling onValueChange with:', isoValue);
      props.onValueChange(isoValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      console.log('Enter/Tab pressed, stopping editing');
      if (props.stopEditing) {
        props.stopEditing();
      }
    } else if (e.key === 'Escape') {
      if (props.stopEditing) {
        props.stopEditing(true);  // Cancel
      }
    }
  };

  const handleClear = () => {
    console.log('DatePicker clear clicked');
    setDateValue('');
    // Notify grid of cleared value
    if (props.onValueChange) {
      props.onValueChange(null);
    }
    if (props.stopEditing) {
      props.stopEditing();
    }
  };

  const handleSave = () => {
    console.log('DatePicker save clicked, dateValue:', dateValue);
    if (props.stopEditing) {
      props.stopEditing();
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-4 min-w-[280px]" onClick={e => e.stopPropagation()}>
      <div className="mb-3">
        <label className="block text-xs text-slate-400 mb-1">Select Date</label>
        <input
          type="date"
          value={dateValue}
          onChange={handleDateChange}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]"
          style={{ colorScheme: 'dark' }}
        />
      </div>
      <div className="flex gap-2 pt-3 border-t border-slate-700">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleClear}
          className="flex-1 text-slate-400 hover:text-white hover:bg-slate-700"
        >
          Clear
        </Button>
        <Button 
          size="sm" 
          onClick={handleSave}
          className="flex-1 bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
        >
          Save
        </Button>
      </div>
    </div>
  );
});

// =====================
// MAIN AG GRID COMPONENT
// =====================

const AGGridWBS = ({
  tasks,
  calendars,
  resources,
  project,
  baselines,
  activeBaseline,
  onTaskUpdate,
  onTaskAdd,
  onTaskDelete,
  onIndent,
  onOutdent,
  onCopyTask,
  onPasteTask,
  onInsertRow,
  onBulkPasteRows,
  onMoveToParent,
  onMoveToChild,
  onCreateBaseline,
  onSetActiveBaseline,
  onDeleteBaseline,
  onExportBaseline,
  onRecalculate,
  onOpenVersions,
  userRole = 'planner',
  parentProjectId,
  scheduleId,
  customFieldSchema = [],
  selectedCustomCols = [],
  setSelectedCustomCols = () => {},
  onAttachDocs = null,
  attachmentCounts: attachmentCountsProp = {},
  onDeleteEntireSchedule = null,
  onBulkAttachDocs = null,
}) => {
  const gridRef = useRef(null);
  const [contextMenuPosition, setContextMenuPosition] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  // Linked discipline schedules (Engineering / Procurement / F&C / Pre-comm)
  const [linkSummary, setLinkSummary] = useState({});
  const [linksActivity, setLinksActivity] = useState(null);

  const loadLinkSummary = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const { data } = await api.get(`/macro-links/schedules/${scheduleId}/summary`);
      setLinkSummary(data.activities || {});
    } catch { /* the column simply shows "+ link" when the summary is unavailable */ }
  }, [scheduleId]);

  useEffect(() => { loadLinkSummary(); }, [loadLinkSummary]);
  // Context changes don't re-render AG Grid cells on their own — refresh the Links column once the summary arrives
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ columns: ['discipline_links', 'rolled_up_pct'], force: true });
  }, [linkSummary]);
  
  // Row clipboard for multi-row copy/paste
  const [rowClipboard, setRowClipboard] = useState([]);
  
  // Selection state
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  
  // Undo/Redo state
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  // Dialog states
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [predecessorDialogOpen, setPredecessorDialogOpen] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [connectorImportOpen, setConnectorImportOpen] = useState(false);
  const [excelDialogTab, setExcelDialogTab] = useState('export');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  
  // Predecessor dialog state
  const [predecessorSearch, setPredecessorSearch] = useState('');
  const [selectedPredecessors, setSelectedPredecessors] = useState([]);
  
  // Resource dialog state
  const [selectedResources, setSelectedResources] = useState([]);

  // Filter panel state
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    wbsLevel: [],
    taskName: [],
    startDateRange: [null, null],
    finishDateRange: [null, null],
    actualStartRange: [null, null],
    actualFinishRange: [null, null],
    progressRange: [0, 100],
    status: [],
    criticality: []
  });
  const [filterSearches, setFilterSearches] = useState({
    wbsLevel: '',
    taskName: '',
    status: ''
  });

  // Get unique values for dropdown filters
  const filterOptions = useMemo(() => {
    if (!tasks || tasks.length === 0) return { wbsLevels: [], taskNames: [], statuses: [] };
    
    const wbsLevels = [...new Set(tasks.map(t => t.outline_level || 1))].sort((a, b) => a - b);
    const taskNames = [...new Set(tasks.map(t => t.name).filter(Boolean))].sort();
    const statuses = [...TASK_STATUSES];
    
    // Get date ranges
    const parseDate = (d) => d ? new Date(d).getTime() : null;
    const startDates = tasks.map(t => parseDate(t.scheduled_start)).filter(Boolean);
    const finishDates = tasks.map(t => parseDate(t.scheduled_finish)).filter(Boolean);
    const actualStarts = tasks.map(t => parseDate(t.actual_start)).filter(Boolean);
    const actualFinishes = tasks.map(t => parseDate(t.actual_finish)).filter(Boolean);
    
    return {
      wbsLevels,
      taskNames,
      statuses,
      startDateMin: startDates.length ? new Date(Math.min(...startDates)) : null,
      startDateMax: startDates.length ? new Date(Math.max(...startDates)) : null,
      finishDateMin: finishDates.length ? new Date(Math.min(...finishDates)) : null,
      finishDateMax: finishDates.length ? new Date(Math.max(...finishDates)) : null,
      actualStartMin: actualStarts.length ? new Date(Math.min(...actualStarts)) : null,
      actualStartMax: actualStarts.length ? new Date(Math.max(...actualStarts)) : null,
      actualFinishMin: actualFinishes.length ? new Date(Math.min(...actualFinishes)) : null,
      actualFinishMax: actualFinishes.length ? new Date(Math.max(...actualFinishes)) : null
    };
  }, [tasks]);

  // Status for filtering — the same ladder the column renders.
  const getTaskStatus = useCallback((data) => computeTaskStatus(data), []);

  // One-glance health: how many activities sit in each status today. WBS
  // summary rows are excluded so a slip is not counted twice.
  const statusCounts = useMemo(() => {
    const byStatus = {};
    let total = 0;
    for (const t of (tasks || [])) {
      if (t.is_summary) continue;
      const s = computeTaskStatus(t);
      byStatus[s] = (byStatus[s] || 0) + 1;
      total += 1;
    }
    return { byStatus, total };
  }, [tasks]);

  // Apply filters using AG Grid external filter
  const isExternalFilterPresent = useCallback(() => {
    return filters.wbsLevel.length > 0 ||
           filters.taskName.length > 0 ||
           filters.status.length > 0 ||
           filters.criticality.length > 0 ||
           filters.progressRange[0] > 0 ||
           filters.progressRange[1] < 100 ||
           filters.startDateRange[0] !== null ||
           filters.startDateRange[1] !== null ||
           filters.finishDateRange[0] !== null ||
           filters.finishDateRange[1] !== null ||
           filters.actualStartRange[0] !== null ||
           filters.actualStartRange[1] !== null ||
           filters.actualFinishRange[0] !== null ||
           filters.actualFinishRange[1] !== null;
  }, [filters]);

  const doesExternalFilterPass = useCallback((node) => {
    const data = node.data;
    if (!data) return true;

    // WBS Level filter
    if (filters.wbsLevel.length > 0) {
      const taskLevel = data.outline_level || 1;
      if (!filters.wbsLevel.includes(taskLevel)) {
        return false;
      }
    }

    // Task Name filter
    if (filters.taskName.length > 0 && !filters.taskName.includes(data.name)) {
      return false;
    }

    // Progress filter
    const progress = data.percent_complete || 0;
    if (progress < filters.progressRange[0] || progress > filters.progressRange[1]) {
      return false;
    }

    // Status filter
    if (filters.status.length > 0) {
      const taskStatus = getTaskStatus(data);
      if (!filters.status.includes(taskStatus)) {
        return false;
      }
    }

    // Criticality filter (Critical / Near-Critical / Non-Critical)
    if (filters.criticality.length > 0) {
      const taskCriticality = data.is_critical
        ? 'critical'
        : (data.is_near_critical ? 'near-critical' : 'non-critical');
      if (!filters.criticality.includes(taskCriticality)) {
        return false;
      }
    }

    // Date range filters
    const checkDateRange = (dateStr, range) => {
      if (!range[0] && !range[1]) return true;
      if (!dateStr) return false;
      const date = new Date(dateStr).getTime();
      if (range[0] && date < range[0]) return false;
      if (range[1] && date > range[1]) return false;
      return true;
    };

    if (!checkDateRange(data.scheduled_start, filters.startDateRange)) return false;
    if (!checkDateRange(data.scheduled_finish, filters.finishDateRange)) return false;
    if (!checkDateRange(data.actual_start, filters.actualStartRange)) return false;
    if (!checkDateRange(data.actual_finish, filters.actualFinishRange)) return false;

    return true;
  }, [filters, getTaskStatus]);

  // Trigger filter update when filters change
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.onFilterChanged();
    }
  }, [filters]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters({
      wbsLevel: [],
      taskName: [],
      startDateRange: [null, null],
      finishDateRange: [null, null],
      actualStartRange: [null, null],
      actualFinishRange: [null, null],
      progressRange: [0, 100],
      status: [],
      criticality: []
    });
    setFilterSearches({ wbsLevel: '', taskName: '', status: '' });
    toast.success('Filters cleared');
  }, []);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.wbsLevel.length > 0) count++;
    if (filters.taskName.length > 0) count++;
    if (filters.status.length > 0) count++;
    if (filters.criticality.length > 0) count++;
    if (filters.progressRange[0] > 0 || filters.progressRange[1] < 100) count++;
    if (filters.startDateRange[0] || filters.startDateRange[1]) count++;
    if (filters.finishDateRange[0] || filters.finishDateRange[1]) count++;
    if (filters.actualStartRange[0] || filters.actualStartRange[1]) count++;
    if (filters.actualFinishRange[0] || filters.actualFinishRange[1]) count++;
    return count;
  }, [filters]);

  // Toolbar handlers
  const handleCopyTask = useCallback((taskId) => {
    const task = tasks?.find(t => t.id === taskId);
    if (!task) return;
    // Deep copy without actuals, progress, baseline
    const copied = {
      ...task,
      actual_start: null,
      actual_finish: null,
      percent_complete: 0,
      baseline_start: null,
      baseline_finish: null,
      id: null, // Will be assigned new ID on paste
      uid: null
    };
    setClipboard(copied);
    toast.success(`Task "${task.name}" copied to clipboard`);
    if (onCopyTask) onCopyTask(taskId);
  }, [tasks, onCopyTask]);

  const handlePasteTask = useCallback((afterTaskId) => {
    if (!clipboard) {
      toast.error('No task in clipboard');
      return;
    }
    if (onPasteTask) {
      onPasteTask(afterTaskId, clipboard);
      toast.success('Task pasted');
    }
  }, [clipboard, onPasteTask]);

  // =====================
  // ROW INSERTION HANDLERS
  // =====================
  
  // Copy selected rows to row clipboard for multi-row paste
  const handleCopySelectedRows = useCallback(() => {
    if (!gridRef.current?.api) return;
    
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (selectedNodes.length === 0) {
      // If no rows selected, copy the focused cell's row
      if (selectedTaskId) {
        const task = tasks?.find(t => t.id === selectedTaskId);
        if (task) {
          setRowClipboard([task]);
          toast.success(`1 row copied to clipboard`);
        }
      }
      return;
    }
    
    const selectedData = selectedNodes.map(node => node.data).filter(Boolean);
    setRowClipboard(selectedData);
    toast.success(`${selectedData.length} row${selectedData.length > 1 ? 's' : ''} copied to clipboard`);
  }, [selectedTaskId, tasks]);

  // Insert row(s) above the selected task
  const handleInsertRowAbove = useCallback((taskId) => {
    if (!taskId) {
      toast.error('No task selected');
      return;
    }
    if (onInsertRow) {
      onInsertRow(taskId, 'above', null, 1);
    }
  }, [onInsertRow]);

  // Insert row(s) below the selected task
  const handleInsertRowBelow = useCallback((taskId) => {
    if (!taskId) {
      toast.error('No task selected');
      return;
    }
    if (onInsertRow) {
      onInsertRow(taskId, 'below', null, 1);
    }
  }, [onInsertRow]);

  // Paste rows from row clipboard (insert, not overwrite)
  const handlePasteRowsBelow = useCallback((taskId) => {
    if (!taskId) {
      toast.error('No task selected');
      return;
    }
    if (rowClipboard.length === 0) {
      toast.error('No rows in clipboard. Use Shift+C to copy rows.');
      return;
    }
    if (onBulkPasteRows) {
      onBulkPasteRows(taskId, 'below', rowClipboard);
    }
  }, [rowClipboard, onBulkPasteRows]);

  // Paste rows above
  const handlePasteRowsAbove = useCallback((taskId) => {
    if (!taskId) {
      toast.error('No task selected');
      return;
    }
    if (rowClipboard.length === 0) {
      toast.error('No rows in clipboard. Use Shift+C to copy rows.');
      return;
    }
    if (onBulkPasteRows) {
      onBulkPasteRows(taskId, 'above', rowClipboard);
    }
  }, [rowClipboard, onBulkPasteRows]);

  // Process clipboard data from external sources (Excel, etc.)
  // This intercepts the paste operation to INSERT rows rather than OVERWRITE
  const processDataFromClipboard = useCallback((params) => {
    const { data } = params;
    
    // If pasting multiple rows from external source
    if (data && data.length > 1) {
      const selectedNode = gridRef.current?.api?.getSelectedNodes()?.[0];
      const referenceTaskId = selectedNode?.data?.id || selectedTaskId;
      
      if (referenceTaskId && onBulkPasteRows) {
        // Parse pasted data into task objects
        const tasksData = data.map(row => {
          if (Array.isArray(row)) {
            // Tab-separated data from Excel
            return {
              name: row[0] || 'Pasted Task',
              duration: row[1] ? parseInt(row[1]) * 8 : 8, // Assume days, convert to hours
              // Add more field mappings as needed
            };
          }
          return row;
        });
        
        // Call the bulk paste handler to INSERT rows
        onBulkPasteRows(referenceTaskId, 'below', tasksData);
        
        // Return null to prevent AG Grid's default paste behavior
        return null;
      }
    }
    
    // For single cell paste, let AG Grid handle it normally
    return data;
  }, [selectedTaskId, onBulkPasteRows]);

  const handleUndo = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.undoCellEditing();
      setCanUndo(gridRef.current.api.getCurrentUndoSize() > 0);
      setCanRedo(gridRef.current.api.getCurrentRedoSize() > 0);
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.redoCellEditing();
      setCanUndo(gridRef.current.api.getCurrentUndoSize() > 0);
      setCanRedo(gridRef.current.api.getCurrentRedoSize() > 0);
    }
  }, []);

  // Cut task handler
  const handleCutTask = useCallback((taskId) => {
    const task = tasks?.find(t => t.id === taskId);
    if (!task) return;
    const copied = {
      ...task,
      _isCut: true,
      actual_start: null,
      actual_finish: null,
      percent_complete: 0,
      baseline_start: null,
      baseline_finish: null
    };
    setClipboard(copied);
    toast.success(`Task "${task.name}" cut to clipboard`);
  }, [tasks]);

  // Link tasks handler - create FS dependencies between selected tasks
  const handleLinkTasks = useCallback((taskIds) => {
    if (!taskIds || taskIds.length < 2) return;
    
    // Sort tasks by position/UID
    const sortedTasks = taskIds
      .map(id => tasks?.find(t => t.id === id))
      .filter(Boolean)
      .sort((a, b) => (a.uid || 0) - (b.uid || 0));
    
    // Create FS links: each task depends on the previous
    for (let i = 1; i < sortedTasks.length; i++) {
      const currentTask = sortedTasks[i];
      const predecessorTask = sortedTasks[i - 1];
      
      const currentDeps = currentTask.dependencies || [];
      const hasDep = currentDeps.some(d => d.predecessor_id === predecessorTask.id);
      
      if (!hasDep) {
        const newDeps = [...currentDeps, {
          predecessor_id: predecessorTask.id,
          type: 'finish_to_start',
          lag: 0
        }];
        if (onTaskUpdate) {
          onTaskUpdate(currentTask.id, { dependencies: newDeps });
        }
      }
    }
    toast.success(`Linked ${sortedTasks.length} tasks with FS dependencies`);
  }, [tasks, onTaskUpdate]);

  // Unlink tasks handler - remove all predecessors
  const handleUnlinkTasks = useCallback((taskId) => {
    const task = tasks?.find(t => t.id === taskId);
    if (!task) return;
    
    if (task.dependencies?.length > 0) {
      if (onTaskUpdate) {
        onTaskUpdate(taskId, { dependencies: [] });
      }
      toast.success(`Removed all predecessors from "${task.name}"`);
    } else {
      toast.info('Task has no predecessors to remove');
    }
  }, [tasks, onTaskUpdate]);

  // Toggle milestone handler
  const handleToggleMilestone = useCallback((taskId) => {
    const task = tasks?.find(t => t.id === taskId);
    if (!task) return;
    
    if (task.is_summary) {
      toast.error('Summary tasks cannot be milestones');
      return;
    }
    
    const newValue = !task.is_milestone;
    const updates = { is_milestone: newValue };
    if (newValue) {
      updates.duration = 0;
    }
    
    if (onTaskUpdate) {
      onTaskUpdate(taskId, updates);
    }
    toast.success(newValue ? 'Marked as milestone' : 'Unmarked as milestone');
  }, [tasks, onTaskUpdate]);

  // Compare baselines handler
  const handleCompareBaselines = useCallback((baseline1, baseline2) => {
    // This would typically navigate to a comparison view or show a dialog
    toast.info(`Comparing Baseline ${baseline1} vs ${baseline2 || 'Current Schedule'}`);
    // The actual comparison logic is in the parent component
  }, []);

  // Auto Calculate Weightage - distributes weights to sum 100% at each WBS level
  // Parent tasks get proportional weights based on their position in the hierarchy
  const handleAutoCalculateWeightage = useCallback(async () => {
    if (!tasks || tasks.length === 0) {
      toast.error('No tasks to calculate weightage');
      return;
    }

    const progressId = toast.loading('Calculating weights from durations…', {
      description: `Analysing ${tasks.length} tasks across ${new Set(tasks.map(t => t.outline_level)).size} WBS levels`,
    });

    // Build parent-children map
    const childrenMap = {};
    const rootTasks = [];
    
    tasks.forEach(task => {
      if (task.parent_id) {
        if (!childrenMap[task.parent_id]) {
          childrenMap[task.parent_id] = [];
        }
        childrenMap[task.parent_id].push(task);
      } else {
        rootTasks.push(task);
      }
    });

    const updates = [];

    // Recursive function to calculate weights proportional to duration within each sibling group
    // Each sibling group sums to 100%. Duration is resolved through the same
    // priority chain as the DUR column so schedules imported as dates-only
    // (Aerovex XER / MSP) still get sensible weights instead of every leaf
    // ending up with the fallback 8-hour default.
    const _dayMs = 86400000;
    const _dur = (t) => {
      if (t.duration && Number.isFinite(t.duration) && t.duration > 0) return t.duration;
      const pairs = [
        [t.scheduled_start, t.scheduled_finish],
        [t.baseline_start, t.baseline_finish],
        [t.early_start, t.early_finish],
      ];
      for (const [s, f] of pairs) {
        if (!s || !f) continue;
        const ds = new Date(s), df = new Date(f);
        if (Number.isNaN(ds.getTime()) || Number.isNaN(df.getTime())) continue;
        const days = Math.max(1, Math.round((df - ds) / _dayMs));
        return days * 8;  // convert to hours to stay consistent with the DB unit
      }
      return 8;  // last-resort fallback (single 8-hour day)
    };
    const calculateWeights = (taskList) => {
      if (!taskList || taskList.length === 0) return;

      // Calculate total duration for this sibling group
      const totalDuration = taskList.reduce((sum, t) => sum + _dur(t), 0);

      taskList.forEach(task => {
        const taskDuration = _dur(task);
        const weight = totalDuration > 0
          ? parseFloat(((taskDuration / totalDuration) * 100).toFixed(2))
          : parseFloat((100 / taskList.length).toFixed(2));

        updates.push({ id: task.id, weight });

        // Recurse into children — each child group independently sums to 100%
        const children = childrenMap[task.id];
        if (children && children.length > 0) {
          calculateWeights(children);
        }
      });
    };

    // Start calculation from root level
    calculateWeights(rootTasks);
    
    // Apply all weight updates via a single bulk endpoint round-trip.
    // This replaces the previous N×PUT loop which timed out at the
    // Cloudflare 502 limit (~30 s) on 285-task schedules.
    if (updates.length > 0) {
      try {
        const payload = Object.fromEntries(updates.map(u => [u.id, u.weight]));
        const t0 = Date.now();
        const { data } = await api.post(
          '/scheduling/tasks/bulk-set-weights?recalculate=false',
          payload,
        );
        const ms = Date.now() - t0;
        // Replace the in-flight loading toast with a detailed success.
        toast.success(`Weightage recalculated`, {
          id: progressId,
          description: `${data?.updated ?? updates.length} tasks · sibling groups sum to 100% · ${ms} ms`,
        });
      } catch (err) {
        toast.error('Failed to apply weights', {
          id: progressId,
          description: err?.response?.data?.detail || err.message,
        });
      } finally {
        if (onRecalculate) onRecalculate();
      }
    } else if (updates.length === 0) {
      toast.info('Nothing to recalculate', { id: progressId });
    }
  }, [tasks, onRecalculate]);

  // ── Auto-Duration from Weight ─────────────────────────────────────────
  // Mirror operation of `handleAutoCalculateWeightage`. For every sibling
  // group, the current total duration (sum of `_dur`) is redistributed
  // proportionally to each child's user-entered `weight`.  Rows whose
  // weight is 0 keep their existing duration (avoids collapsing them to
  // zero).  Summary rows are skipped (they inherit from their children on
  // the next roll-up pass).
  const handleAutoCalculateDuration = useCallback(async () => {
    if (!tasks || tasks.length === 0) {
      toast.error('No tasks to redistribute'); return;
    }
    const progressId = toast.loading('Redistributing durations from weights…');

    // Same date-aware duration resolver used by Auto-Weight.
    const _dayMs = 86400000;
    const _dur = (t) => {
      if (t.duration && Number.isFinite(t.duration) && t.duration > 0) return t.duration;
      const pairs = [
        [t.scheduled_start, t.scheduled_finish],
        [t.baseline_start, t.baseline_finish],
        [t.early_start, t.early_finish],
      ];
      for (const [s, f] of pairs) {
        if (!s || !f) continue;
        const ds = new Date(s), df = new Date(f);
        if (Number.isNaN(ds.getTime()) || Number.isNaN(df.getTime())) continue;
        return Math.max(1, Math.round((df - ds) / _dayMs)) * 8;
      }
      return 8;
    };

    const childrenMap = {}; const rootTasks = [];
    tasks.forEach(t => {
      if (t.parent_id) (childrenMap[t.parent_id] ||= []).push(t);
      else rootTasks.push(t);
    });

    const updates = [];
    const walk = (group) => {
      if (!group?.length) return;
      const totalDur = group.reduce((s, t) => s + _dur(t), 0);
      const totalW   = group.reduce((s, t) => s + (Number(t.weight) || 0), 0);
      if (totalDur > 0 && totalW > 0) {
        group.forEach(t => {
          if (t.is_summary) return;   // summaries derive from children
          const w = Number(t.weight) || 0;
          if (w <= 0) return;         // don't collapse zero-weight rows
          const newHours = Math.max(8, Math.round((totalDur * w / totalW) / 8) * 8);
          if (newHours !== t.duration) {
            updates.push({ id: t.id, duration: newHours });
          }
        });
      }
      group.forEach(t => walk(childrenMap[t.id]));
    };
    walk(rootTasks);

    if (!updates.length) {
      toast.info('Nothing to redistribute — enter weights first', { id: progressId });
      return;
    }
    // Fan out one PUT per row — no bulk endpoint exists for duration yet
    // but we cap concurrency at 20 to protect the API. Total time is
    // <2 s even for 500-task schedules.
    try {
      const t0 = Date.now();
      const chunks = [];
      for (let i = 0; i < updates.length; i += 20) chunks.push(updates.slice(i, i + 20));
      for (const chunk of chunks) {
        await Promise.all(chunk.map(u =>
          api.put(`/scheduling/tasks/${u.id}`, { duration: u.duration, duration_unit: 'hours' })
        ));
      }
      toast.success('Durations redistributed', {
        id: progressId,
        description: `${updates.length} tasks · scaled by weight · ${Date.now() - t0} ms`,
      });
    } catch (err) {
      toast.error('Failed to redistribute durations', {
        id: progressId,
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      if (onRecalculate) onRecalculate();
    }
  }, [tasks, onRecalculate]);
  const handleOpenImportExport = useCallback((tab = 'export') => {    setExcelDialogTab(tab);
    setExcelDialogOpen(true);
  }, []);

  // Handle import complete - refresh tasks
  const handleImportComplete = useCallback((result) => {
    const count = result?.stats?.tasks ?? result?.tasks_imported ?? 0;
    toast.success(`Imported ${count} tasks — refreshing…`);
    const newId = result?.schedule_project_id;
    // If backend created a new schedule_project (legacy path), rewrite the
    // URL so the next reload lands on the populated schedule.
    if (newId && project?.id && newId !== project.id) {
      try {
        const path = window.location.pathname.replace(project.id, newId);
        window.history.replaceState(null, '', path);
      } catch (e) { /* non-fatal */ }
    }
    // Full reload is the most reliable way to repopulate tasks, calendars,
    // resources and metrics after a heavy import. CPM recalculation runs
    // automatically once tasks reload.
    setTimeout(() => window.location.reload(), 600);
  }, [project?.id]);

  // Track selection from AG Grid
  const onRowSelected = useCallback((event) => {
    if (event.node.isSelected()) {
      setSelectedTaskId(event.data?.id);
    }
  }, []);

  const onSelectionChanged = useCallback((event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedIds = selectedNodes.map(node => node.data?.id).filter(Boolean);
    setSelectedTasks(selectedIds);
    if (selectedIds.length > 0) {
      setSelectedTaskId(selectedIds[0]);
    }
  }, []);

  // Transform tasks into AG Grid format
  const rowData = useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return [];
    const sorted = [...tasks].sort((a, b) => {
      const wbsA = (a.wbs_code || '').split('.').map(Number);
      const wbsB = (b.wbs_code || '').split('.').map(Number);
      for (let i = 0; i < Math.max(wbsA.length, wbsB.length); i++) {
        if ((wbsA[i] || 0) !== (wbsB[i] || 0)) return (wbsA[i] || 0) - (wbsB[i] || 0);
      }
      return 0;
    });
    return sorted.map(task => ({
      ...task,
      wbsPath: task.wbs_code ? task.wbs_code.split('.') : [String(task.uid || '1')]
    }));
  }, [tasks]);

  // Grid context
  const gridContext = useMemo(() => ({
    tasks, calendars, resources, project,
    onAttachDocs,
    attachmentCounts: attachmentCountsProp || {},
    linkSummary: linkSummary,
    openMacroLinks: (task) => setLinksActivity(task),    openCalendarDialog: (taskId, task) => {
      setEditingTaskId(taskId);
      setEditingTask(task);
      setCalendarDialogOpen(true);
    },
    openResourceDialog: (taskId, task) => {
      setEditingTaskId(taskId);
      setEditingTask(task);
      setSelectedResources(task.resource_assignments || []);
      setResourceDialogOpen(true);
    },
    openPredecessorDialog: (taskId, task) => {
      setEditingTaskId(taskId);
      setEditingTask(task);
      setSelectedPredecessors((task.dependencies || []).map(d => ({
        predecessor_id: d.predecessor_id,
        type: d.type || 'finish_to_start',
        lag: d.lag || 0
      })));
      setPredecessorDialogOpen(true);
    }
  }), [tasks, calendars, resources, project, onAttachDocs, attachmentCountsProp, linkSummary]);

  // Column definitions - Serial # integrated into tree column
  const columnDefs = useMemo(() => [
    {
      headerName: 'Task Name',
      field: 'name',
      minWidth: 220,
      flex: 1,
      pinned: 'left',
      editable: true,
      cellRenderer: TaskNameRenderer,
      cellClass: params => params.data?.is_summary ? 'font-semibold text-white' : 'text-slate-200'
    },
    {
      headerName: 'Contractor',
      field: 'contractor',
      width: 160,
      minWidth: 120,
      editable: false,
      headerTooltip: 'Construction Contractor — cascaded from Battery Limits (Interface & BL Management)',
      cellRenderer: (params) => {
        const v = (params.value || '').trim();
        const src = params.data?.contractorSource;
        if (!v) return <span className="text-slate-600 text-[10px] italic">—</span>;
        const dot = src === 'BL' ? '#10b981' : src === 'MANUAL' ? '#f59e0b' : '#64748b';
        return (
          <span className="inline-flex items-center gap-1.5 text-xs" title={`Source: ${src || 'unknown'}`}>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 9999, background: dot, flex: 'none' }} />
            <span className="truncate text-slate-200">{v}</span>
          </span>
        );
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      headerName: 'MS',
      field: 'is_milestone',
      width: 50,
      minWidth: 50,
      cellRenderer: MilestoneRenderer,
      editable: false,
      cellClass: 'text-center',
      headerTooltip: 'Milestone'
    },
    {
      headerName: 'Dur',
      field: 'duration',
      width: 70,
      minWidth: 70,
      cellRenderer: DurationRenderer,
      editable: params => !params.data?.is_summary && !params.data?.is_milestone,
      headerTooltip: 'Duration',
      valueParser: params => {
        const match = String(params.newValue).match(/^(\d+(?:\.\d+)?)\s*(d|h|w)?$/i);
        if (!match) return params.oldValue;
        const num = parseFloat(match[1]);
        const unit = (match[2] || 'd').toLowerCase();
        if (unit === 'h') return num;
        if (unit === 'w') return num * 5 * 8;
        return num * 8;
      }
    },
    {
      headerName: 'Start',
      field: 'scheduled_start',
      width: 110,
      minWidth: 110,
      cellRenderer: DateRenderer,
      // Editable for all leaf tasks. When a task has predecessors, editing
      // Start acts as a manual override that survives until the user clicks
      // Recalculate (CPM).
      editable: params => !params.data?.is_summary,
      cellEditor: DatePickerCellEditor,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      singleClickEdit: true,
      headerTooltip: 'Scheduled Start Date (click to edit · overrides CPM until next Recalculate)',
      cellClass: params => params.data?.is_summary ? 'text-slate-400' : 'cursor-pointer'
    },
    {
      headerName: 'Finish',
      field: 'scheduled_finish',
      width: 100,
      minWidth: 100,
      cellRenderer: DateRenderer,
      // Editable for leaf tasks — duration is auto-recomputed on save.
      editable: params => !params.data?.is_summary,
      cellEditor: DatePickerCellEditor,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      singleClickEdit: true,
      headerTooltip: 'Scheduled Finish Date (click to edit · duration auto-recomputes)',
      cellClass: params => params.data?.is_summary ? 'text-slate-400' : 'cursor-pointer'
    },
    {
      headerName: 'BL Start',
      field: 'baseline_start',
      width: 100,
      minWidth: 100,
      cellRenderer: DateRenderer,
      editable: false,
      cellClass: 'text-slate-500',
      headerTooltip: 'Baseline Start Date'
    },
    {
      headerName: 'BL Finish',
      field: 'baseline_finish',
      width: 100,
      minWidth: 100,
      cellRenderer: DateRenderer,
      editable: false,
      cellClass: 'text-slate-500',
      headerTooltip: 'Baseline Finish Date'
    },
    {
      headerName: 'Act Start',
      field: 'actual_start',
      width: 110,
      minWidth: 110,
      cellRenderer: DateRenderer,
      editable: params => !params.data?.is_summary, // Summary tasks: read-only
      cellEditor: DatePickerCellEditor,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      singleClickEdit: true, // Enable single-click editing for easier access
      headerTooltip: 'Actual Start Date (Click to edit)'
    },
    {
      headerName: 'Act Finish',
      field: 'actual_finish',
      width: 110,
      minWidth: 110,
      cellRenderer: DateRenderer,
      editable: params => !params.data?.is_summary, // Summary tasks: read-only
      cellEditor: DatePickerCellEditor,
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      singleClickEdit: true, // Enable single-click editing for easier access
      headerTooltip: 'Actual Finish Date (Click to edit)'
    },
    {
      headerName: 'Pred',
      field: 'dependencies',
      width: 80,
      minWidth: 80,
      cellRenderer: PredecessorRenderer,
      editable: false,
      headerTooltip: 'Predecessors',
      onCellClicked: params => {
        if (params.context?.openPredecessorDialog) {
          params.context.openPredecessorDialog(params.data.id, params.data);
        }
      },
      cellClass: 'cursor-pointer hover:bg-slate-700/30'
    },
    {
      headerName: 'Cal',
      field: 'calendar_id',
      width: 70,
      minWidth: 70,
      cellRenderer: CalendarRenderer,
      editable: false,
      headerTooltip: 'Calendar',
      onCellClicked: params => {
        if (params.context?.openCalendarDialog) {
          params.context.openCalendarDialog(params.data.id, params.data);
        }
      },
      cellClass: 'cursor-pointer hover:bg-slate-700/30'
    },
    {
      headerName: 'Res',
      field: 'resource_assignments',
      width: 70,
      minWidth: 70,
      cellRenderer: ResourceRenderer,
      editable: false,
      headerTooltip: 'Resources',
      onCellClicked: params => {
        if (params.context?.openResourceDialog) {
          params.context.openResourceDialog(params.data.id, params.data);
        }
      },
      cellClass: 'cursor-pointer hover:bg-slate-700/30'
    },
    {
      headerName: 'Const',
      field: 'constraint_type',
      width: 70,
      minWidth: 70,
      cellRenderer: ConstraintRenderer,
      editable: true,
      headerTooltip: 'Constraint Type',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['as_soon_as_possible', 'as_late_as_possible', 'start_no_earlier_than', 'finish_no_earlier_than', 'must_start_on', 'must_finish_on']
      }
    },
    {
      headerName: 'Wt%',
      field: 'weight',
      width: 65,
      minWidth: 65,
      editable: params => !params.data?.is_summary,
      headerTooltip: 'Weight Percentage',
      valueFormatter: params => {
        // Show `—` for null/undefined (never auto-weighted) so we can visually
        // distinguish "not yet computed" from a genuine 0%.
        const v = params.value;
        if (v == null || v === '') return '—';
        return `${parseFloat(v).toFixed(1)}%`;
      },
      valueParser: params => Math.min(100, Math.max(0, parseFloat(params.newValue) || 0)),
      cellClass: 'text-right text-slate-400'
    },
    {
      headerName: 'Prog%',
      field: 'percent_complete',
      width: 85,
      minWidth: 85,
      cellRenderer: ProgressRenderer,
      editable: params => !params.data?.is_summary,
      headerTooltip: 'Progress Percentage',
      valueParser: params => Math.min(100, Math.max(0, parseInt(params.newValue) || 0))
    },
    {
      headerName: 'Rolled-Up %',
      colId: 'rolled_up_pct',
      width: 110,
      minWidth: 100,
      valueGetter: (params) => {
        const rc = params.data?.rollUpConfig;
        if (rc?.rollUpEnabled && rc.rolledUpProgressPct != null) return rc.rolledUpProgressPct;
        // Fall back to the discipline links, so this column and the Links column agree.
        return params.context?.linkSummary?.[params.data?.id]?.macroProgressPct ?? null;
      },
      cellRenderer: (params) => {
        const rc = params.data?.rollUpConfig;
        const summary = params.context?.linkSummary?.[params.data?.id];
        const rolled = (rc?.rollUpEnabled && rc.rolledUpProgressPct != null)
          ? rc.rolledUpProgressPct
          : summary?.macroProgressPct;
        if (rolled == null) {
          return <span style={{ color: '#64748b', fontSize: 11 }}>—</span>;
        }
        const status = rc?.rollUpStatus
          || (summary?.lastPulledAt ? 'SYNCED' : (summary?.totalLinks ? 'STALE' : 'UNLINKED'));
        const own    = params.data?.percent_complete || 0;
        const delta  = rolled != null ? Math.round((rolled - own) * 10) / 10 : null;
        const statusColour = {
          SYNCED:  '#10b981',
          PARTIAL: '#f59e0b',
          STALE:   '#ef4444',
          UNLINKED:'#64748b',
        }[status] || '#64748b';
        const pctText = rolled != null ? `${Math.round(rolled * 10) / 10}%` : '—';
        const deltaColor = delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : '#64748b';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 9999, background: statusColour, marginRight: 6 }} />
            <span style={{ fontWeight: 600, color: statusColour, fontSize: 12 }}>{pctText}</span>
            {delta != null && (
              <span style={{ color: deltaColor, fontSize: 10, marginLeft: 4 }}>
                ({delta >= 0 ? '+' : ''}{delta})
              </span>
            )}
          </span>
        );
      },
      editable: false,
      headerTooltip: 'Weighted-average progress rolled up from linked micros (DCI/MCI/F&C/PCCM). Delta shows divergence vs own progress.',
      sortable: true,
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      headerName: 'Links',
      colId: 'discipline_links',
      width: 172,
      minWidth: 172,
      editable: false,
      sortable: false,
      filter: false,
      headerTooltip: 'Linked Engineering / Procurement / Fabrication & Construction / Pre-commissioning activities. Click to view them and pull progress into this activity.',
      cellRenderer: (params) => {
        const ctx = params.context || {};
        const summary = ctx.linkSummary?.[params.data?.id];
        return (
          <DisciplineChips
            summary={summary}
            onOpen={() => ctx.openMacroLinks?.(params.data)}
            testId={`discipline-links-${params.data?.id}`}
          />
        );
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 100,
      minWidth: 90,
      cellRenderer: StatusRenderer,
      editable: false,
      headerTooltip: 'Task Status (CPM-based: Not Started / In Progress / Completed with delay/ahead indicators)',
      sortable: true,
      comparator: (valueA, valueB, nodeA, nodeB) => {
        // Custom sort order for status
        const getStatusOrder = (data) => {
          if (!data) return 99;
          const prog = data.percent_complete || 0;
          if (prog >= 100) return 1; // Completed first
          if (prog > 0) return 2;    // In Progress
          return 3;                   // Not Started
        };
        return getStatusOrder(nodeA.data) - getStatusOrder(nodeB.data);
      }
    },
    {
      headerName: 'AI',
      colId: 'ai_slip_recovery',
      width: 56,
      minWidth: 56,
      maxWidth: 64,
      cellRenderer: SlipRecoveryCell,
      cellRendererParams: { projectId: parentProjectId, scheduleId },
      editable: false,
      sortable: false,
      filter: false,
      headerTooltip: 'AI slip-recovery suggestion (Cognara)',
      cellClass: 'text-center',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    },
    // Dynamic columns from MPP/XER extended attributes (Text1, Number1, Date1, …).
    // Surfaced only when the user toggles them on via the "Columns" picker.
    ...selectedCustomCols
      .map((key) => customFieldSchema.find((s) => s.key === key))
      .filter(Boolean)
      .map((s) => ({
        headerName: s.label,
        field: `custom_fields.${s.key}`,
        width: s.type === 'date' ? 120 : (s.type === 'number' || s.type === 'cost') ? 100 : 140,
        editable: false,
        cellClass: 'text-slate-300 text-xs',
        valueGetter: (p) => p.data?.custom_fields?.[s.key],
        valueFormatter: (p) => {
          const v = p.value;
          if (v == null || v === '') return '';
          if (s.type === 'date' && typeof v === 'string') {
            try { return new Date(v).toLocaleDateString(); } catch { return v; }
          }
          if (s.type === 'boolean') return v ? '✓' : '';
          if ((s.type === 'number' || s.type === 'cost') && typeof v === 'number') {
            return s.type === 'cost' ? `$${v.toFixed(2)}` : (Number.isInteger(v) ? String(v) : v.toFixed(2));
          }
          return String(v);
        },
        headerTooltip: `${s.label} • imported from MS Project ${s.key} (${s.populated_count} tasks have a value)`,
      })),
  ], [parentProjectId, scheduleId, selectedCustomCols, customFieldSchema]);

  // Default column definition - AG Grid v35 compliant
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
    suppressHeaderMenuButton: true,
    suppressMenu: true
  }), []);

  // Auto group column for tree data - includes # and WBS
  const autoGroupColumnDef = useMemo(() => ({
    headerName: '#  |  WBS',
    field: 'wbs_code',
    width: 150,
    minWidth: 130,
    pinned: 'left',
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: params => {
        const rowIndex = params.node?.rowIndex ?? 0;
        const wbs = params.data?.wbs_code || '';
        return (
          <span className="flex items-center gap-2">
            <span className="text-slate-500 font-mono text-xs w-6">{rowIndex + 1}</span>
            <span className="text-slate-400 font-mono text-xs">{wbs}</span>
          </span>
        );
      }
    }
  }), []);

  // Get data path for tree structure - use wbs_path from backend or fallback to uid
  const getDataPath = useCallback((data) => data.wbs_path || data.wbsPath || [data.id], []);

  // Handle cell value changes
  const onCellValueChanged = useCallback((event) => {
    const { data, colDef, newValue, oldValue } = event;
    if (newValue === oldValue) return;
    const field = colDef.field;
    const updates = { [field]: newValue };
    if (field === 'duration') updates.duration_unit = 'hours';
    // When the user explicitly edits Finish on a leaf task, recompute the
    // duration from (Finish - Start) so the bar length stays in sync. CPM
    // recalculation can still be invoked manually via the toolbar.
    if (field === 'scheduled_finish' && data && !data.is_summary) {
      const start = data.scheduled_start ? new Date(data.scheduled_start) : null;
      const finish = newValue ? new Date(newValue) : null;
      if (start && finish && !Number.isNaN(start.getTime()) && !Number.isNaN(finish.getTime())) {
        const days = Math.max(0, Math.round((finish - start) / 86400000));
        updates.duration = days;
        updates.duration_unit = 'days';
      }
    }
    if (onTaskUpdate) onTaskUpdate(data.id, updates);
  }, [onTaskUpdate]);

  // Context menu
  const onCellContextMenu = useCallback((event) => {
    event.event.preventDefault();
    setSelectedNode(event.node);
    setContextMenuPosition({ x: event.event.clientX, y: event.event.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenuPosition(null);
    setSelectedNode(null);
  }, []);

  const handleContextMenuAction = useCallback((action) => {
    if (!selectedNode) return;
    const data = selectedNode.data;
    switch (action) {
      case 'insertAbove': 
        // Use new row insertion API for proper position handling
        if (onInsertRow) {
          onInsertRow(data.id, 'above', null, 1);
        } else if (onTaskAdd) {
          onTaskAdd(data.id, 'above');
        }
        break;
      case 'insertBelow': 
        // Use new row insertion API for proper position handling
        if (onInsertRow) {
          onInsertRow(data.id, 'below', null, 1);
        } else if (onTaskAdd) {
          onTaskAdd(data.id, 'below');
        }
        break;
      case 'insertChild': if (onTaskAdd) onTaskAdd(data.id, 'child'); break;
      case 'delete': if (onTaskDelete) onTaskDelete(data.id); break;
      case 'indent': if (onIndent) onIndent(data.id); break;
      case 'outdent': if (onOutdent) onOutdent(data.id); break;
      case 'milestone': handleToggleMilestone(data.id); break;
      case 'predecessor': gridContext.openPredecessorDialog(data.id, data); break;
      case 'resource': gridContext.openResourceDialog(data.id, data); break;
      case 'calendar': gridContext.openCalendarDialog(data.id, data); break;
      case 'copy': handleCopyTask(data.id); break;
      case 'copyRows': handleCopySelectedRows(); break;
      case 'cut': handleCutTask(data.id); break;
      case 'paste': handlePasteTask(data.id); break;
      case 'pasteRowsBelow': handlePasteRowsBelow(data.id); break;
      case 'pasteRowsAbove': handlePasteRowsAbove(data.id); break;
      case 'unlink': handleUnlinkTasks(data.id); break;
    }
    closeContextMenu();
  }, [selectedNode, onTaskAdd, onInsertRow, onTaskDelete, onIndent, onOutdent, closeContextMenu, gridContext, handleCopyTask, handleCopySelectedRows, handleCutTask, handlePasteTask, handlePasteRowsBelow, handlePasteRowsAbove, handleUnlinkTasks, handleToggleMilestone]);

  // Row class rules
  const rowClassRules = useMemo(() => ({
    'ag-row-critical': params => params.data?.is_critical,
    'ag-row-near-critical': params => !params.data?.is_critical && params.data?.is_near_critical,
    'ag-row-completed': params => params.data?.percent_complete === 100,
    'ag-row-milestone': params => params.data?.is_milestone,
    'ag-row-summary': params => params.data?.is_summary
  }), []);

  const getRowId = useCallback((params) => params.data.id, []);

  // Clipboard handling
  const processCellForClipboard = useCallback((params) => {
    const field = params.column.getColDef().field;
    if (field === 'calendar_id') return JSON.stringify({ type: 'calendar', calendar_id: params.value });
    if (field === 'resource_assignments') return JSON.stringify({ type: 'resource', resource_assignments: params.value || [] });
    if (field === 'dependencies') return JSON.stringify({ type: 'predecessor', dependencies: params.value || [] });
    return params.value;
  }, []);

  const processCellFromClipboard = useCallback((params) => {
    const field = params.column.getColDef().field;
    try {
      const parsed = JSON.parse(params.value);
      if (parsed.type === 'calendar' && field === 'calendar_id') return parsed.calendar_id;
      if (parsed.type === 'resource' && field === 'resource_assignments') return parsed.resource_assignments;
      if (parsed.type === 'predecessor' && field === 'dependencies') return parsed.dependencies;
    } catch {}
    return params.value;
  }, []);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => closeContextMenu();
    if (contextMenuPosition) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenuPosition, closeContextMenu]);

  // Save calendar selection
  const handleCalendarSave = () => {
    if (editingTaskId && editingTask) {
      onTaskUpdate(editingTaskId, { calendar_id: editingTask.calendar_id });
    }
    setCalendarDialogOpen(false);
  };

  // Save resource selection
  const handleResourceSave = () => {
    if (editingTaskId) {
      onTaskUpdate(editingTaskId, { resource_assignments: selectedResources });
    }
    setResourceDialogOpen(false);
  };

  // Save predecessor selection
  const handlePredecessorSave = () => {
    if (editingTaskId) {
      onTaskUpdate(editingTaskId, { dependencies: selectedPredecessors });
    }
    setPredecessorDialogOpen(false);
  };

  // Filter tasks for predecessor selection (exclude current task and its descendants)
  const availablePredecessors = useMemo(() => {
    if (!editingTask || !tasks) return [];
    const currentWbs = editingTask.wbs_code || '';
    return tasks.filter(t => {
      if (t.id === editingTask.id) return false;
      // Exclude descendants
      if (t.wbs_code?.startsWith(currentWbs + '.')) return false;
      return true;
    });
  }, [editingTask, tasks]);

  const filteredPredecessors = useMemo(() => {
    if (!predecessorSearch) return availablePredecessors;
    const search = predecessorSearch.toLowerCase();
    return availablePredecessors.filter(t => 
      t.name?.toLowerCase().includes(search) || 
      t.wbs_code?.toLowerCase().includes(search) ||
      String(t.uid).includes(search)
    );
  }, [availablePredecessors, predecessorSearch]);

  return (
    <div className="ag-grid-wbs-container h-full w-full relative flex flex-col">
      {/* WBS Toolbar */}
      <WBSToolbar
        gridRef={gridRef}
        tasks={tasks}
        project={project}
        scheduleId={scheduleId}
        linkedActivityCount={Object.keys(linkSummary || {}).length}
        onPullAllLinks={() => { loadLinkSummary(); onRecalculate?.(); }}
        baselines={baselines}
        activeBaseline={activeBaseline}
        selectedTaskId={selectedTaskId}
        selectedTasks={selectedTasks}
        clipboard={clipboard}
        rowClipboard={rowClipboard}
        onAddTask={onTaskAdd}
        onDeleteTask={onTaskDelete}
        onIndent={onIndent}
        onOutdent={onOutdent}
        onCopyTask={handleCopyTask}
        onCutTask={handleCutTask}
        onPasteTask={handlePasteTask}
        onCopyRows={handleCopySelectedRows}
        onPasteRowsBelow={handlePasteRowsBelow}
        onInsertRow={onInsertRow}
        onMoveToParent={onMoveToParent}
        onMoveToChild={onMoveToChild}
        onCreateBaseline={onCreateBaseline}
        onSetActiveBaseline={onSetActiveBaseline}
        onDeleteBaseline={onDeleteBaseline}
        onExportBaseline={onExportBaseline}
        onCompareBaselines={handleCompareBaselines}
        onRecalculate={onRecalculate}
        onOpenVersions={onOpenVersions}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onToggleMilestone={handleToggleMilestone}
        onLinkTasks={handleLinkTasks}
        onUnlinkTasks={handleUnlinkTasks}
        onOpenImportExport={handleOpenImportExport}
        onOpenConnectorImport={() => setConnectorImportOpen(true)}
        onAutoCalculateWeightage={handleAutoCalculateWeightage}
        onAutoCalculateDuration={handleAutoCalculateDuration}
        onDeleteEntireSchedule={onDeleteEntireSchedule}
        onBulkAttachDocs={onBulkAttachDocs ? () => onBulkAttachDocs(selectedTasks) : null}
        canUndo={canUndo}
        canRedo={canRedo}
        userRole={userRole}
      />

      {/* Custom-field column picker — surfaces MPP/XER extended attributes (Text1/Number1/…). */}
      {customFieldSchema.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-900/40 border-b border-slate-700 text-xs">
          <span className="text-slate-400">Custom fields:</span>
          <div className="flex flex-wrap gap-1.5 flex-1">
            {customFieldSchema.map((s) => {
              const active = selectedCustomCols.includes(s.key);
              return (
                <button
                  key={s.key}
                  data-testid={`custom-col-${s.key}`}
                  onClick={() => setSelectedCustomCols(
                    active
                      ? selectedCustomCols.filter((k) => k !== s.key)
                      : [...selectedCustomCols, s.key]
                  )}
                  className={
                    `px-2 py-0.5 rounded-full border text-[10.5px] transition ` +
                    (active
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                      : 'bg-slate-800/60 text-slate-400 border-slate-700 hover:border-slate-500')
                  }
                  title={`${s.label} · ${s.type} · ${s.populated_count} tasks populated`}
                >
                  {active ? '✓ ' : '+ '}{s.label}
                  <span className="ml-1 text-slate-500">{s.populated_count}</span>
                </button>
              );
            })}
          </div>
          {selectedCustomCols.length > 0 && (
            <button
              onClick={() => setSelectedCustomCols([])}
              className="text-slate-500 hover:text-slate-300 px-1.5"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Status health strip — counts by status, click to filter */}
      {statusCounts.total > 0 && (
        <div className="px-4 py-2 border-b border-slate-700 bg-slate-900/40 flex items-center gap-2 flex-wrap"
             data-testid="status-health-strip">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">
            {statusCounts.total} activities
          </span>
          {STRIP_ORDER.filter(s => statusCounts.byStatus[s]).map(s => {
            const style = STATUS_STYLES[s] || {};
            const on = filters.status.includes(s);
            return (
              <button
                key={s}
                onClick={() => setFilters(f => ({
                  ...f,
                  status: f.status.includes(s) ? f.status.filter(x => x !== s) : [...f.status, s],
                }))}
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-all ${style.bg} ${style.text} ${on ? 'ring-2 ring-offset-0 ring-slate-400' : 'hover:brightness-125'}`}
                title={`${statusCounts.byStatus[s]} ${s} — click to ${on ? 'clear' : 'filter'}`}
                data-testid={`status-strip-${s.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
              >
                {style.short || s} <b className="ml-0.5">{statusCounts.byStatus[s]}</b>
              </button>
            );
          })}
          {filters.status.length > 0 && (
            <button onClick={() => setFilters(f => ({ ...f, status: [] }))}
                    className="text-[11px] text-slate-400 hover:text-slate-200 underline ml-1"
                    data-testid="status-strip-clear">
              show all
            </button>
          )}
        </div>
      )}

      {/* Collapsible Filter Panel */}
      <Collapsible open={filterPanelOpen} onOpenChange={setFilterPanelOpen}>
        <div className="border-b border-slate-700 bg-slate-900/50">
          <CollapsibleTrigger asChild>
            <div className="w-full px-4 py-2 flex items-center justify-between hover:bg-slate-800/50 transition-colors cursor-pointer">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-300">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge className="bg-[#1E4E8C] text-white text-xs px-1.5 py-0 h-5">
                    {activeFilterCount}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {activeFilterCount > 0 && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); resetFilters(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); resetFilters(); } }}
                    className="h-6 px-2 text-xs text-slate-400 hover:text-white flex items-center gap-1 rounded hover:bg-slate-700/50 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" /> Clear
                  </span>
                )}
                {filterPanelOpen ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </div>
            </div>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="px-4 pb-4 pt-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
              
              {/* WBS Level Filter */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">WBS Level</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs">
                      {filters.wbsLevel.length > 0 ? `${filters.wbsLevel.length} selected` : 'All levels'}
                      <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2 bg-slate-800 border-slate-600" align="start">
                    <div className="relative mb-2">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                      <Input
                        placeholder="Search..."
                        value={filterSearches.wbsLevel}
                        onChange={(e) => setFilterSearches(s => ({ ...s, wbsLevel: e.target.value }))}
                        className="h-7 pl-7 text-xs bg-slate-900 border-slate-600"
                      />
                    </div>
                    <ScrollArea className="h-40">
                      <div className="space-y-1">
                        {filterOptions.wbsLevels
                          .filter(l => String(l).includes(filterSearches.wbsLevel))
                          .map(level => (
                          <label key={level} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-700 cursor-pointer">
                            <Checkbox
                              checked={filters.wbsLevel.includes(level)}
                              onCheckedChange={(checked) => {
                                setFilters(f => ({
                                  ...f,
                                  wbsLevel: checked 
                                    ? [...f.wbsLevel, level]
                                    : f.wbsLevel.filter(l => l !== level)
                                }));
                              }}
                              className="w-3.5 h-3.5"
                            />
                            <span className="text-xs text-slate-200">Level {level}</span>
                          </label>
                        ))}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Task Name Filter */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Task Name</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs truncate">
                      {filters.taskName.length > 0 ? `${filters.taskName.length} selected` : 'All tasks'}
                      <ChevronDown className="w-3 h-3 ml-1 opacity-50 flex-shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2 bg-slate-800 border-slate-600" align="start">
                    <div className="relative mb-2">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                      <Input
                        placeholder="Search tasks..."
                        value={filterSearches.taskName}
                        onChange={(e) => setFilterSearches(s => ({ ...s, taskName: e.target.value }))}
                        className="h-7 pl-7 text-xs bg-slate-900 border-slate-600"
                      />
                    </div>
                    <ScrollArea className="h-48">
                      <div className="space-y-1">
                        {filterOptions.taskNames
                          .filter(n => n.toLowerCase().includes(filterSearches.taskName.toLowerCase()))
                          .map(name => (
                          <label key={name} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-700 cursor-pointer">
                            <Checkbox
                              checked={filters.taskName.includes(name)}
                              onCheckedChange={(checked) => {
                                setFilters(f => ({
                                  ...f,
                                  taskName: checked 
                                    ? [...f.taskName, name]
                                    : f.taskName.filter(n => n !== name)
                                }));
                              }}
                              className="w-3.5 h-3.5"
                            />
                            <span className="text-xs text-slate-200 truncate">{name}</span>
                          </label>
                        ))}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Start Date Range */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs">
                      {filters.startDateRange[0] || filters.startDateRange[1] ? 'Filtered' : 'Any'}
                      <CalendarIcon className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3 bg-slate-800 border-slate-600" align="start">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">From</Label>
                        <Calendar
                          mode="single"
                          selected={filters.startDateRange[0] ? new Date(filters.startDateRange[0]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, startDateRange: [date?.getTime() || null, f.startDateRange[1]] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">To</Label>
                        <Calendar
                          mode="single"
                          selected={filters.startDateRange[1] ? new Date(filters.startDateRange[1]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, startDateRange: [f.startDateRange[0], date?.getTime() || null] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setFilters(f => ({ ...f, startDateRange: [null, null] }))} className="w-full text-xs">
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Finish Date Range */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Finish Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs">
                      {filters.finishDateRange[0] || filters.finishDateRange[1] ? 'Filtered' : 'Any'}
                      <CalendarIcon className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3 bg-slate-800 border-slate-600" align="start">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">From</Label>
                        <Calendar
                          mode="single"
                          selected={filters.finishDateRange[0] ? new Date(filters.finishDateRange[0]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, finishDateRange: [date?.getTime() || null, f.finishDateRange[1]] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">To</Label>
                        <Calendar
                          mode="single"
                          selected={filters.finishDateRange[1] ? new Date(filters.finishDateRange[1]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, finishDateRange: [f.finishDateRange[0], date?.getTime() || null] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setFilters(f => ({ ...f, finishDateRange: [null, null] }))} className="w-full text-xs">
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Actual Start Date Range */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Actual Start</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs">
                      {filters.actualStartRange[0] || filters.actualStartRange[1] ? 'Filtered' : 'Any'}
                      <CalendarIcon className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3 bg-slate-800 border-slate-600" align="start">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">From</Label>
                        <Calendar
                          mode="single"
                          selected={filters.actualStartRange[0] ? new Date(filters.actualStartRange[0]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, actualStartRange: [date?.getTime() || null, f.actualStartRange[1]] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">To</Label>
                        <Calendar
                          mode="single"
                          selected={filters.actualStartRange[1] ? new Date(filters.actualStartRange[1]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, actualStartRange: [f.actualStartRange[0], date?.getTime() || null] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setFilters(f => ({ ...f, actualStartRange: [null, null] }))} className="w-full text-xs">
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Actual Finish Date Range */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Actual Finish</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs">
                      {filters.actualFinishRange[0] || filters.actualFinishRange[1] ? 'Filtered' : 'Any'}
                      <CalendarIcon className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3 bg-slate-800 border-slate-600" align="start">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">From</Label>
                        <Calendar
                          mode="single"
                          selected={filters.actualFinishRange[0] ? new Date(filters.actualFinishRange[0]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, actualFinishRange: [date?.getTime() || null, f.actualFinishRange[1]] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 mb-1 block">To</Label>
                        <Calendar
                          mode="single"
                          selected={filters.actualFinishRange[1] ? new Date(filters.actualFinishRange[1]) : undefined}
                          onSelect={(date) => setFilters(f => ({ ...f, actualFinishRange: [f.actualFinishRange[0], date?.getTime() || null] }))}
                          className="rounded-md border-slate-600"
                        />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setFilters(f => ({ ...f, actualFinishRange: [null, null] }))} className="w-full text-xs">
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Progress % Range */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Progress %</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs">
                      {filters.progressRange[0] > 0 || filters.progressRange[1] < 100 
                        ? `${filters.progressRange[0]}% - ${filters.progressRange[1]}%`
                        : '0% - 100%'}
                      <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4 bg-slate-800 border-slate-600" align="start">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-xs text-slate-300">
                        <span>{filters.progressRange[0]}%</span>
                        <span>{filters.progressRange[1]}%</span>
                      </div>
                      <Slider
                        value={filters.progressRange}
                        onValueChange={(value) => setFilters(f => ({ ...f, progressRange: value }))}
                        min={0}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setFilters(f => ({ ...f, progressRange: [0, 0] }))} className="flex-1 text-xs">
                          Not Started
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setFilters(f => ({ ...f, progressRange: [100, 100] }))} className="flex-1 text-xs">
                          Complete
                        </Button>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setFilters(f => ({ ...f, progressRange: [0, 100] }))} className="w-full text-xs">
                        Reset
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Status Filter */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Status</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs"
                            data-testid="filter-status-trigger">
                      {filters.status.length > 0 ? `${filters.status.length} selected` : 'All statuses'}
                      <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2 bg-slate-800 border-slate-600" align="start">
                    <div className="relative mb-2">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                      <Input
                        placeholder="Search status..."
                        value={filterSearches.status}
                        onChange={(e) => setFilterSearches(s => ({ ...s, status: e.target.value }))}
                        className="h-7 pl-7 text-xs bg-slate-900 border-slate-600"
                      />
                    </div>
                    <ScrollArea className="h-48">
                      <div className="space-y-1">
                        {filterOptions.statuses
                          .filter(s => s.toLowerCase().includes(filterSearches.status.toLowerCase()))
                          .map(status => {
                            const style = STATUS_STYLES[status] || {};
                            return (
                              <label key={status} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-700 cursor-pointer"
                                     data-testid={`filter-status-option-${status.replace(/[^a-z]+/gi, '-').toLowerCase()}`}>
                                <Checkbox
                                  checked={filters.status.includes(status)}
                                  onCheckedChange={(checked) => {
                                    setFilters(f => ({
                                      ...f,
                                      status: checked 
                                        ? [...f.status, status]
                                        : f.status.filter(s => s !== status)
                                    }));
                                  }}
                                  className="w-3.5 h-3.5"
                                />
                                <span className={`text-xs px-1.5 py-0.5 rounded ${style.bg || ''} ${style.text || ''}`}>{status}</span>
                              </label>
                            );
                          })}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Criticality Filter */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Criticality</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-between h-8 bg-slate-800 border-slate-600 text-slate-200 text-xs"
                      data-testid="filter-criticality-trigger"
                    >
                      {filters.criticality.length > 0
                        ? (filters.criticality.length === 1
                            ? ({ 'critical': 'Critical only', 'near-critical': 'Near-critical only', 'non-critical': 'Non-critical only' }[filters.criticality[0]])
                            : `${filters.criticality.length} selected`)
                        : 'All activities'}
                      <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2 bg-slate-800 border-slate-600" align="start">
                    <div className="space-y-1">
                      {[
                        { key: 'critical', label: 'Critical path', dot: 'bg-red-500', pill: 'bg-red-500/20 text-red-300 border-red-500/40' },
                        { key: 'near-critical', label: 'Near-critical', dot: 'bg-amber-500', pill: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
                        { key: 'non-critical', label: 'Non-critical', dot: 'bg-slate-500', pill: 'bg-slate-600/30 text-slate-300 border-slate-500/40' }
                      ].map(opt => (
                        <label
                          key={opt.key}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-700 cursor-pointer"
                          data-testid={`filter-criticality-option-${opt.key}`}
                        >
                          <Checkbox
                            checked={filters.criticality.includes(opt.key)}
                            onCheckedChange={(checked) => {
                              setFilters(f => ({
                                ...f,
                                criticality: checked
                                  ? [...f.criticality, opt.key]
                                  : f.criticality.filter(k => k !== opt.key)
                              }));
                            }}
                            className="w-3.5 h-3.5"
                          />
                          <span className={`inline-block w-2 h-2 rounded-full ${opt.dot}`} />
                          <span className={`text-xs px-1.5 py-0.5 rounded border ${opt.pill}`}>{opt.label}</span>
                        </label>
                      ))}
                      <div className="border-t border-slate-700 my-1" />
                      <button
                        type="button"
                        className="w-full text-left px-2 py-1.5 text-xs text-[#4A90D9] hover:bg-slate-700 rounded"
                        onClick={() => setFilters(f => ({ ...f, criticality: ['critical'] }))}
                        data-testid="filter-criticality-only-critical"
                      >
                        Show critical path only
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-2 py-1.5 text-xs text-slate-400 hover:bg-slate-700 rounded"
                        onClick={() => setFilters(f => ({ ...f, criticality: [] }))}
                        data-testid="filter-criticality-clear"
                      >
                        Clear
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* AG Grid */}
      <div className="ag-theme-alpine-dark flex-1 w-full" style={{ minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={gridContext}
          theme="legacy"
          
          // Tree data
          treeData={true}
          getDataPath={getDataPath}
          groupDefaultExpanded={-1}
          autoGroupColumnDef={autoGroupColumnDef}
          excludeChildrenWhenTreeDataFiltering={true}
          
          // Selection - AG Grid v35 syntax
          rowSelection={{ mode: 'multiRow', enableClickSelection: true }}
          cellSelection={{ handle: true }}
          undoRedoCellEditing={true}
          undoRedoCellEditingLimit={20}
          
          // Row config
          getRowId={getRowId}
          rowClassRules={rowClassRules}
          rowHeight={36}
          headerHeight={40}
          
          // Events
          onCellValueChanged={onCellValueChanged}
          onCellContextMenu={onCellContextMenu}
          preventDefaultOnContextMenu={true}
          suppressContextMenu={true}
          onRowSelected={onRowSelected}
          onSelectionChanged={onSelectionChanged}
          
          // Clipboard
          processCellForClipboard={processCellForClipboard}
          processCellFromClipboard={processCellFromClipboard}
          
          // External filtering
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          
          // Performance
          animateRows={true}
          suppressScrollOnNewData={true}
          suppressColumnVirtualisation={true}
          
          // Auto-size columns on ready
          onGridReady={params => {
            // Size columns to fit content on initial load
            setTimeout(() => {
              params.api.sizeColumnsToFit();
            }, 100);
          }}
        />
      </div>
      
      {/* Context Menu */}
      {contextMenuPosition && (
        <div 
          className="fixed bg-slate-800 border border-slate-600 rounded-lg shadow-2xl py-1 z-50 min-w-56"
          style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}
          data-testid="context-menu"
        >
          {/* Insert Section */}
          <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">Insert</div>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('insertAbove')} data-testid="ctx-insert-above">
            <Plus className="w-4 h-4 text-slate-400" /> Insert Task Above
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('insertBelow')} data-testid="ctx-insert-below">
            <Plus className="w-4 h-4 text-slate-400" /> Insert Task Below
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('insertChild')} data-testid="ctx-insert-child">
            <Plus className="w-4 h-4 text-[#1E4E8C]" /> Insert Child Task
          </button>
          
          <div className="border-t border-slate-700 my-1" />
          
          {/* Edit Section */}
          <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">Edit</div>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('copy')} data-testid="ctx-copy">
            <Copy className="w-4 h-4 text-slate-400" /> Copy Cell
            <span className="ml-auto text-slate-500 text-xs">⌘C</span>
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('copyRows')} data-testid="ctx-copy-rows">
            <Copy className="w-4 h-4 text-[#1E4E8C]" /> Copy Row(s)
            <span className="ml-auto text-slate-500 text-xs">⇧C</span>
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('cut')} data-testid="ctx-cut">
            <Scissors className="w-4 h-4 text-slate-400" /> Cut
            <span className="ml-auto text-slate-500 text-xs">⌘X</span>
          </button>
          <button 
            className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-700 flex items-center gap-3 ${clipboard ? 'text-slate-200' : 'text-slate-500'}`} 
            onClick={() => handleContextMenuAction('paste')}
            disabled={!clipboard}
            data-testid="ctx-paste"
          >
            <Clipboard className="w-4 h-4" /> Paste (Overwrite)
            <span className="ml-auto text-slate-500 text-xs">⌘V</span>
          </button>
          
          {/* Insert Paste Section */}
          <div className="border-t border-slate-700 my-1" />
          <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">Insert Paste</div>
          <button 
            className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-700 flex items-center gap-3 ${rowClipboard.length > 0 ? 'text-slate-200' : 'text-slate-500'}`}
            onClick={() => handleContextMenuAction('pasteRowsAbove')}
            disabled={rowClipboard.length === 0}
            data-testid="ctx-paste-rows-above"
          >
            <Plus className="w-4 h-4" /> Paste Rows Above
            {rowClipboard.length > 0 && <span className="ml-auto text-[#1E4E8C] text-xs">{rowClipboard.length} row{rowClipboard.length > 1 ? 's' : ''}</span>}
          </button>
          <button 
            className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-700 flex items-center gap-3 ${rowClipboard.length > 0 ? 'text-slate-200' : 'text-slate-500'}`}
            onClick={() => handleContextMenuAction('pasteRowsBelow')}
            disabled={rowClipboard.length === 0}
            data-testid="ctx-paste-rows-below"
          >
            <Plus className="w-4 h-4" /> Paste Rows Below
            {rowClipboard.length > 0 && <span className="ml-auto text-[#1E4E8C] text-xs">{rowClipboard.length} row{rowClipboard.length > 1 ? 's' : ''}</span>}
          </button>
          
          <div className="border-t border-slate-700 my-1" />
          
          {/* Hierarchy Section */}
          <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">Hierarchy</div>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('indent')} data-testid="ctx-indent">
            <ChevronRight className="w-4 h-4 text-slate-400" /> Indent
            <span className="ml-auto text-slate-500 text-xs">Tab</span>
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('outdent')} data-testid="ctx-outdent">
            <ChevronLeft className="w-4 h-4 text-slate-400" /> Outdent
            <span className="ml-auto text-slate-500 text-xs">⇧Tab</span>
          </button>
          
          <div className="border-t border-slate-700 my-1" />
          
          {/* Dependencies Section */}
          <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">Dependencies</div>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('predecessor')} data-testid="ctx-predecessor">
            <Link2 className="w-4 h-4 text-slate-400" /> Edit Predecessors
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('unlink')} data-testid="ctx-unlink">
            <Unlink className="w-4 h-4 text-slate-400" /> Remove All Predecessors
          </button>
          
          <div className="border-t border-slate-700 my-1" />
          
          {/* Task Properties */}
          <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">Properties</div>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('resource')} data-testid="ctx-resource">
            <Users className="w-4 h-4 text-slate-400" /> Assign Resources
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('calendar')} data-testid="ctx-calendar">
            <Calendar className="w-4 h-4 text-slate-400" /> Assign Calendar
          </button>
          {!selectedNode?.data?.is_summary && (
            <button className="w-full px-4 py-2 text-left text-sm text-amber-400 hover:bg-slate-700 flex items-center gap-3" onClick={() => handleContextMenuAction('milestone')} data-testid="ctx-milestone">
              <Diamond className="w-4 h-4" /> Toggle Milestone
            </button>
          )}

          {onAttachDocs && (
            <button className="w-full px-4 py-2 text-left text-sm text-sky-300 hover:bg-slate-700 flex items-center gap-3"
              onClick={() => { onAttachDocs(selectedNode?.data); closeContextMenu(); }}
              data-testid="ctx-attach-docs">
              <Paperclip className="w-4 h-4 text-sky-400" /> Attach Documents…
            </button>
          )}

          <div className="border-t border-slate-700 my-1" />
          
          {/* Delete */}
          <button className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3" onClick={() => handleContextMenuAction('delete')} data-testid="ctx-delete">
            <X className="w-4 h-4" /> Delete Task
            <span className="ml-auto text-slate-500 text-xs">Del</span>
          </button>
        </div>
      )}

      {/* Calendar Selection Dialog - Enhanced UI */}
      <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#1E4E8C]" />
              Select Activity Calendar
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Task info header */}
            <div className="bg-slate-900/50 rounded-lg p-3 mb-4 border border-slate-700">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400">Activity:</span>
                <span className="text-white font-medium">{editingTask?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="text-slate-400">Current Calendar:</span>
                <span className="text-[#1E4E8C]">
                  {calendars.find(c => c.id === editingTask?.calendar_id)?.name || 'None assigned'}
                </span>
              </div>
            </div>
            
            {/* Search filter */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search calendars..."
                className="pl-10 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                onChange={(e) => {
                  // Filter logic handled in render
                }}
                id="calendar-search"
              />
            </div>
            
            {/* Calendar list */}
            <ScrollArea className="h-[280px] pr-4">
              <div className="space-y-2">
                {/* Default option to remove calendar */}
                <div 
                  onClick={() => setEditingTask({ ...editingTask, calendar_id: null })}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    !editingTask?.calendar_id 
                      ? 'border-slate-500 bg-slate-700/30' 
                      : 'border-slate-600 hover:border-slate-500 bg-slate-900/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      !editingTask?.calendar_id ? 'bg-slate-600' : 'bg-slate-800'
                    }`}>
                      <X className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-300 font-medium">No Calendar (Use Project Default)</p>
                      <p className="text-slate-500 text-sm">Inherit from project settings</p>
                    </div>
                    {!editingTask?.calendar_id && (
                      <Check className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
                
                {calendars.map(cal => {
                  const isSelected = editingTask?.calendar_id === cal.id;
                  const workingDaysStr = cal.working_days?.map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]).join(', ') || 'Mon-Fri';
                  const hoursPerDay = cal.hours_per_day || 8;
                  
                  return (
                    <div 
                      key={cal.id}
                      onClick={() => setEditingTask({ ...editingTask, calendar_id: cal.id })}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-[#1E4E8C] bg-[#1E4E8C]/10' 
                          : 'border-slate-600 hover:border-slate-500 bg-slate-900/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-[#1E4E8C]/20' : 'bg-slate-800'
                        }`}>
                          <CalendarDays className={`w-5 h-5 ${isSelected ? 'text-[#1E4E8C]' : 'text-slate-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                            {cal.name}
                          </p>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-slate-400 text-xs flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {hoursPerDay}h/day
                            </span>
                            <span className="text-slate-500 text-xs">{workingDaysStr}</span>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-[#1E4E8C] flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {calendars.length === 0 && (
                  <div className="text-center py-8">
                    <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No calendars available</p>
                    <p className="text-slate-500 text-sm mt-1">Create one in the Calendars tab</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="outline" onClick={() => setCalendarDialogOpen(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleCalendarSave} className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90">
              <Check className="w-4 h-4 mr-2" /> Assign Calendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resource Selection Dialog - Enhanced UI with Multi-Select */}
      <Dialog open={resourceDialogOpen} onOpenChange={setResourceDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" />
              Assign Resources
              {selectedResources.length > 0 && (
                <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 border-0">
                  {selectedResources.length} selected
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Task info header */}
            <div className="bg-slate-900/50 rounded-lg p-3 mb-4 border border-slate-700">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400">Activity:</span>
                <span className="text-white font-medium">{editingTask?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="text-slate-400">Current Resources:</span>
                <span className="text-emerald-400">
                  {selectedResources.length > 0 
                    ? `${selectedResources.length} assigned` 
                    : 'None assigned'}
                </span>
              </div>
            </div>
            
            {/* Quick actions */}
            <div className="flex items-center gap-2 mb-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSelectedResources(resources.map(r => r.id))}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs"
              >
                Select All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSelectedResources([])}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs"
              >
                Clear All
              </Button>
              <div className="flex-1" />
              <span className="text-slate-500 text-xs">
                {resources.length} resources available
              </span>
            </div>
            
            {/* Search filter */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search resources by name or role..."
                className="pl-10 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                id="resource-search"
              />
            </div>
            
            {/* Resource list */}
            <ScrollArea className="h-[320px] pr-4">
              <div className="space-y-2">
                {resources.map(res => {
                  const isSelected = selectedResources.includes(res.id);
                  const resourceType = res.type || 'labor';
                  const typeColors = {
                    'labor': 'bg-blue-500/20 text-blue-400',
                    'equipment': 'bg-amber-500/20 text-amber-400',
                    'material': 'bg-purple-500/20 text-purple-400'
                  };
                  
                  return (
                    <div 
                      key={res.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedResources(selectedResources.filter(id => id !== res.id));
                        } else {
                          setSelectedResources([...selectedResources, res.id]);
                        }
                      }}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-emerald-500 bg-emerald-500/10' 
                          : 'border-slate-600 hover:border-slate-500 bg-slate-900/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Avatar/Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          isSelected 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-slate-700 text-slate-300'
                        }`}>
                          {res.initials || res.name?.substring(0, 2).toUpperCase() || <User className="w-5 h-5" />}
                        </div>
                        
                        {/* Resource info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium truncate ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                              {res.name}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[resourceType] || typeColors.labor}`}>
                              {resourceType}
                            </span>
                          </div>
                          <p className="text-slate-400 text-sm truncate">
                            {res.role || res.email || 'No role assigned'}
                          </p>
                        </div>
                        
                        {/* Checkbox */}
                        <Checkbox 
                          checked={isSelected} 
                          className={`w-5 h-5 ${isSelected ? 'data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500' : ''}`}
                        />
                      </div>
                    </div>
                  );
                })}
                {resources.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No resources available</p>
                    <p className="text-slate-500 text-sm mt-1">Create resources in the Resources tab</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="outline" onClick={() => setResourceDialogOpen(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleResourceSave} 
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Check className="w-4 h-4 mr-2" /> 
              Assign {selectedResources.length > 0 ? `(${selectedResources.length})` : ''} Resources
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Predecessor Selection Dialog */}
      <Dialog open={predecessorDialogOpen} onOpenChange={setPredecessorDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#1E4E8C]" />
              Manage Predecessors
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-2 min-h-0">
            <Label className="text-slate-300 mb-3 block">Predecessors for: <span className="text-white font-medium">{editingTask?.name}</span></Label>
            
            {/* Current Predecessors */}
            {selectedPredecessors.length > 0 && (
              <div className="mb-4">
                <p className="text-slate-400 text-sm mb-2">Current Predecessors ({selectedPredecessors.length}):</p>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {selectedPredecessors.map((pred, idx) => {
                    const predTask = tasks.find(t => t.id === pred.predecessor_id);
                    if (!predTask) return null;
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg border border-slate-700">
                        <div className="flex-1">
                          <span className="text-[#1E4E8C] font-mono text-sm mr-2">#{predTask.uid}</span>
                          <span className="text-white">{predTask.name}</span>
                        </div>
                        <Select 
                          value={pred.type} 
                          onValueChange={(val) => {
                            const updated = [...selectedPredecessors];
                            updated[idx].type = val;
                            setSelectedPredecessors(updated);
                          }}
                        >
                          <SelectTrigger className="w-24 bg-slate-800 border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="finish_to_start">FS</SelectItem>
                            <SelectItem value="start_to_start">SS</SelectItem>
                            <SelectItem value="finish_to_finish">FF</SelectItem>
                            <SelectItem value="start_to_finish">SF</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-400 text-sm">Lag:</span>
                          <Input 
                            type="number" 
                            value={pred.lag || 0}
                            onChange={(e) => {
                              const updated = [...selectedPredecessors];
                              updated[idx].lag = parseInt(e.target.value) || 0;
                              setSelectedPredecessors(updated);
                            }}
                            className="w-16 bg-slate-800 border-slate-600 text-center"
                          />
                          <span className="text-slate-400 text-sm">d</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedPredecessors(selectedPredecessors.filter((_, i) => i !== idx))}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Search and Add */}
            <div className="border-t border-slate-700 pt-4">
              <p className="text-slate-400 text-sm mb-2">Add Predecessor:</p>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input 
                  placeholder="Search by name, WBS, or #..."
                  value={predecessorSearch}
                  onChange={(e) => setPredecessorSearch(e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-600"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredPredecessors.map(task => {
                  const isAlreadyAdded = selectedPredecessors.some(p => p.predecessor_id === task.id);
                  return (
                    <div 
                      key={task.id}
                      onClick={() => {
                        if (!isAlreadyAdded) {
                          setSelectedPredecessors([...selectedPredecessors, {
                            predecessor_id: task.id,
                            type: 'finish_to_start',
                            lag: 0
                          }]);
                        }
                      }}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        isAlreadyAdded 
                          ? 'bg-slate-700/30 opacity-50 cursor-not-allowed' 
                          : 'hover:bg-slate-700/50'
                      }`}
                    >
                      <span className="text-[#1E4E8C] font-mono text-sm w-8">#{task.uid}</span>
                      <span className="text-slate-400 font-mono text-xs w-16">{task.wbs_code}</span>
                      <span className="text-white flex-1 truncate">{task.name}</span>
                      {!isAlreadyAdded && (
                        <Plus className="w-4 h-4 text-[#1E4E8C]" />
                      )}
                      {isAlreadyAdded && (
                        <span className="text-emerald-400 text-xs">Added</span>
                      )}
                    </div>
                  );
                })}
                {filteredPredecessors.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No matching tasks found</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-700 shrink-0 bg-slate-800">
            <Button variant="outline" onClick={() => setPredecessorDialogOpen(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={handlePredecessorSave} className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90">
              Save Predecessors ({selectedPredecessors.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Linked discipline schedules for one macro activity */}
      {linksActivity && (
        <MacroLinksDrawer
          open={!!linksActivity}
          onOpenChange={(v) => { if (!v) setLinksActivity(null); }}
          scheduleId={scheduleId}
          activity={linksActivity}
          onPulled={(activityId, after) => {
            loadLinkSummary();
            onTaskUpdate?.(activityId, { percent_complete: after });
          }}
        />
      )}

      {/* Excel Import/Export Dialog */}
      <ExcelImportExportDialog
        open={excelDialogOpen}
        onOpenChange={setExcelDialogOpen}
        projectId={project?.id}
        projectName={project?.name}
        tasks={tasks}
        selectedTaskIds={selectedTasks}
        visibleColumns={columnDefs.filter(c => !c.hide).map(c => c.field)}
        onImportComplete={handleImportComplete}
        onExportComplete={() => toast.success('Export completed')}
      />
      <ConnectorScheduleImportDialog
        open={connectorImportOpen}
        onOpenChange={setConnectorImportOpen}
        portfolioProjectId={project?.portfolio_project_id}
        onImported={handleImportComplete}
      />
    </div>
  );
};

export default AGGridWBS;
